/* Glow Beauty — minimal section interactivity
 * Handles:
 *  - glow-results-compare slider drag/range
 *  - glow-shoppable-look hotspot toggle
 * Scoped per section, idempotent on theme-editor reloads.
 */
(function () {
  'use strict';

  function initCompare(root) {
    if (root.__glowCompareBound) return;
    root.__glowCompareBound = true;
    var range = root.querySelector('[data-glow-compare-range]');
    if (!range) return;

    function setPos(value) {
      root.style.setProperty('--glow-slider-pos', value + '%');
    }
    setPos(range.value);

    range.addEventListener('input', function () {
      setPos(range.value);
    });
  }

  function initShoppable(root) {
    if (root.__glowShoppableBound) return;
    root.__glowShoppableBound = true;

    var hotspots = root.querySelectorAll('[data-glow-hotspot]');
    var cards = root.querySelectorAll('[data-glow-hotspot-card]');

    function closeAll() {
      hotspots.forEach(function (h) { h.setAttribute('aria-expanded', 'false'); });
      cards.forEach(function (c) { c.setAttribute('aria-hidden', 'true'); });
    }

    hotspots.forEach(function (hotspot) {
      hotspot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var open = hotspot.getAttribute('aria-expanded') === 'true';
        var card = root.querySelector('#' + hotspot.getAttribute('aria-controls'));
        closeAll();
        if (!open && card) {
          hotspot.setAttribute('aria-expanded', 'true');
          card.setAttribute('aria-hidden', 'false');
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) closeAll();
    });
  }

  function initRevealGroup(group) {
    if (group.__glowRevealBound) return;
    group.__glowRevealBound = true;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      group.classList.add('is-revealed');
      return;
    }

    Array.prototype.forEach.call(group.children, function (child, i) {
      child.style.setProperty('--glow-reveal-i', i);
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    observer.observe(group);
  }

  function initCardCarousel(root) {
    if (root.__glowCardsBound) return;
    root.__glowCardsBound = true;

    var track = root.querySelector('[data-glow-cards-track]');
    var next = root.querySelector('[data-glow-cards-next]');
    if (!track || !next) return;

    function atEnd() {
      return track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    }
    function sync() {
      next.classList.toggle('is-end', atEnd());
    }

    next.addEventListener('click', function () {
      var card = track.querySelector('.glow-product-cards__card-wrap');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      var step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      // land exactly on the next snap point so mandatory snapping can't bounce back
      var target = Math.round(track.scrollLeft / step + 1) * step;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
      window.setTimeout(sync, 600);
    });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  function initUgcReel(root) {
    if (root.__glowUgcBound) return;
    root.__glowUgcBound = true;

    var track = root.querySelector('[data-glow-ugc-track]');
    if (!track) return;
    var prev = root.querySelector('[data-glow-ugc-prev]');
    var next = root.querySelector('[data-glow-ugc-next]');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Arrow paging — land exactly on snap points */
    function step() {
      var item = track.querySelector('.glow-ugc__item');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    }
    function sync() {
      if (prev) prev.classList.toggle('is-start', track.scrollLeft <= 4);
      if (next) next.classList.toggle('is-end', track.scrollLeft >= track.scrollWidth - track.clientWidth - 4);
    }
    function page(direction) {
      var target = Math.round(track.scrollLeft / step() + direction) * step();
      track.scrollTo({ left: Math.max(0, target), behavior: reduced ? 'auto' : 'smooth' });
      window.setTimeout(sync, 600);
    }
    if (next) next.addEventListener('click', function () { page(1); });
    if (prev) prev.addEventListener('click', function () { page(-1); });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();

    /* Videos: play muted while in view, pause out of view */
    var videos = Array.prototype.slice.call(track.querySelectorAll('video'));
    videos.forEach(function (video) {
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.removeAttribute('controls');
    });

    if (videos.length && 'IntersectionObserver' in window) {
      var watcher = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.intersectionRatio >= 0.5) {
              var playing = video.play();
              if (playing && playing.catch) playing.catch(function () {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.5] }
      );
      videos.forEach(function (video) { watcher.observe(video); });
    }

    /* Tap-to-play fallback when autoplay is off (reduced motion) */
    if (reduced) {
      videos.forEach(function (video) {
        video.addEventListener('click', function () {
          if (video.paused) { video.play().catch(function () {}); } else { video.pause(); }
        });
      });
    }

    /* Sound toggles — one unmuted video at a time */
    root.querySelectorAll('[data-glow-ugc-sound]').forEach(function (button) {
      button.addEventListener('click', function () {
        var video = button.closest('.glow-ugc__media').querySelector('video');
        if (!video) return;
        var unmuting = video.muted;
        if (unmuting) {
          videos.forEach(function (other) {
            if (other !== video) other.muted = true;
          });
          root.querySelectorAll('[data-glow-ugc-sound]').forEach(function (other) {
            other.setAttribute('aria-pressed', 'false');
            other.setAttribute('aria-label', 'Unmute video');
          });
        }
        video.muted = !unmuting;
        button.setAttribute('aria-pressed', unmuting ? 'true' : 'false');
        button.setAttribute('aria-label', unmuting ? 'Mute video' : 'Unmute video');
        if (unmuting && video.paused) video.play().catch(function () {});
      });
    });
  }

  function initQuickAdd(form) {
    if (form.__glowQaddBound) return;
    form.__glowQaddBound = true;

    form.addEventListener('submit', function (event) {
      if (!window.fetch) return; // native POST fallback
      event.preventDefault();

      var btn = form.querySelector('[data-glow-qadd-btn]');
      var label = form.querySelector('[data-glow-qadd-label]');
      if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
      var idle = label ? label.textContent : '';

      btn.classList.add('is-loading');
      if (label) label.textContent = 'Adding…';

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: Number(new FormData(form).get('id')),
          quantity: Number(new FormData(form).get('quantity')) || 1
        })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          if (window.upcartAPI && typeof window.upcartAPI.openCart === 'function') {
            window.upcartAPI.openCart();
          }
          btn.classList.remove('is-loading');
          if (label) label.textContent = 'Added ✓';
          window.setTimeout(function () { if (label) label.textContent = idle; }, 2500);
        })
        .catch(function () {
          btn.classList.remove('is-loading');
          if (label) label.textContent = 'Try again';
          window.setTimeout(function () { if (label) label.textContent = idle; }, 2500);
        });
    });
  }

  // B2B quantity rules (Shopify company price lists set min / increment).
  // Stiletto's steppers are hardcoded to +/-1 and read the input value right
  // before adjusting, so this capture-phase listener runs first and pre-sets
  // the value such that stock theme.js lands exactly on the rule-compliant
  // quantity. Inputs without a rule (retail: min 1, increment 1) are ignored,
  // so retail behavior is byte-for-byte stock.
  function initB2bQuantityRules() {
    document.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var isAdd = event.target.closest('[data-add-quantity]');
      var isSubtract = !isAdd && event.target.closest('[data-subtract-quantity]');
      if (!isAdd && !isSubtract) return;

      var wrapper = (isAdd || isSubtract).closest('.quantity-input');
      if (!wrapper) return;
      var input = wrapper.querySelector('[data-quantity-input]');
      if (!input) return;

      var min = parseInt(input.getAttribute('data-qty-min'), 10) || 1;
      var increment = parseInt(input.getAttribute('data-qty-increment'), 10) || 1;
      if (min <= 1 && increment <= 1) return; // retail: leave stock behavior alone

      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = min;
      var inCartLine = !!wrapper.closest('[data-input-item]');
      var desired;

      if (isAdd) {
        desired = current + increment;
      } else {
        desired = current - increment;
        if (desired < min) {
          // In the cart, stepping below the minimum removes the line;
          // on a product form it just clamps to the minimum.
          desired = inCartLine ? 0 : min;
        }
      }

      // Compensate for theme.js's upcoming +/-1 so it computes `desired`.
      input.value = isAdd ? desired - 1 : desired + 1;
    }, true);
  }
  initB2bQuantityRules();

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-glow-compare]').forEach(initCompare);
    (scope || document).querySelectorAll('[data-glow-shoppable]').forEach(initShoppable);
    (scope || document).querySelectorAll('[data-glow-reveal-group]').forEach(initRevealGroup);
    (scope || document).querySelectorAll('[data-glow-cards]').forEach(initCardCarousel);
    (scope || document).querySelectorAll('[data-glow-ugc]').forEach(initUgcReel);
    (scope || document).querySelectorAll('[data-glow-qadd]').forEach(initQuickAdd);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init when sections are loaded/reordered
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:reorder', function (e) { initAll(e.target); });

  // Toggle body.glow-cart-open whenever Upcart's drawer is visible.
  // CSS uses this class to hide overlays (Smile launcher, etc) that
  // would otherwise sit on top of the cart.
  function initCartOpenWatcher() {
    var DRAWER_SELECTORS = '[id^="upcart"], [class*="upcart"], .Upcart-Cart, .upcart-cart, .upcart-drawer';
    var BODY_CLASS = 'glow-cart-open';
    var lastState = null;

    function isDrawerOpen() {
      var nodes = document.querySelectorAll(DRAWER_SELECTORS);
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        // Skip the embed/script tags injected by the app block
        if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') continue;
        var rect = el.getBoundingClientRect();
        if (rect.width > 100 && rect.height > 100) {
          var style = window.getComputedStyle(el);
          if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0.1) {
            return true;
          }
        }
      }
      return false;
    }

    function syncBodyClass() {
      var open = isDrawerOpen();
      if (open === lastState) return;
      lastState = open;
      document.body.classList.toggle(BODY_CLASS, open);
    }

    var mo = new MutationObserver(function () {
      // Throttle: run once per animation frame
      if (mo._raf) return;
      mo._raf = requestAnimationFrame(function () {
        mo._raf = null;
        syncBodyClass();
      });
    });
    mo.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['style', 'class', 'aria-hidden'] });
    syncBodyClass();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartOpenWatcher);
  } else {
    initCartOpenWatcher();
  }
})();
